import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import { Checkbox, FormControlLabel, Stack, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColumnVisibilityModel } from '@mui/x-data-grid';

import { API } from '@luxuryescapes/lib-types';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { ColumnControlForm } from '~/components/Purchases/Home/AllOrders/ColumnControlForm';

import { useSessionStorage } from '~/hooks/useSessionStorage';

import OrdersCardList from './OrdersCardList';
import { columns as defaultColumns } from './consts';

const LOCAL_STORE_KEY = 'OrdersPage.all-orders-column-control';

interface Props {
  purchases: Array<API.Order.Order>;
  total: number;
  page: number;
  sizePerPage: number;
  onPageChange: (page: number) => void;
  columns?: typeof defaultColumns;
  storageKey?: string;
}

const getGridVisibilityInit = (gridColumns: typeof defaultColumns) => {
  return gridColumns.reduce((returnVal, column) => {
    returnVal[column.field] = true;
    return returnVal;
  }, {} as GridColumnVisibilityModel);
};

export default function OrdersPage({
  page,
  onPageChange,
  sizePerPage,
  purchases,
  total,
  columns = defaultColumns,
  storageKey = LOCAL_STORE_KEY,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const customerId = urlParams.get('customer_id');
  const gridVisibilityInit = useMemo(() => getGridVisibilityInit(columns), [columns]);
  const [storedVisibility, setStoredVisibility] = useSessionStorage<GridColumnVisibilityModel>(
    storageKey,
    gridVisibilityInit,
    (errMsg: string) =>
      enqueueSnackbar(`Error loading existing column visibility settings ${errMsg}`, { variant: 'warning' }),
  );

  const visibleColumns = useMemo(() => {
    return { ...gridVisibilityInit, ...storedVisibility };
  }, [storedVisibility]);

  const setVisibleColumns = useCallback(
    (newValue: GridColumnVisibilityModel | ((prev: GridColumnVisibilityModel) => GridColumnVisibilityModel)) => {
      const updatedValue = typeof newValue === 'function' ? newValue(visibleColumns) : newValue;
      setStoredVisibility(updatedValue);
    },
    [visibleColumns, setStoredVisibility],
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const columnControl = useMemo(() => {
    return columns.map((column) => {
      const checked = visibleColumns[column.field];
      return (
        <FormControlLabel
          key={column.field}
          control={<Checkbox checked={checked} />}
          onChange={() => setVisibleColumns({ ...visibleColumns, [column.field]: !checked })}
          label={column.headerName}
        />
      );
    });
  }, [visibleColumns, setVisibleColumns]);

  // Render mobile card view
  if (isMobile) {
    return (
      <OrdersCardList
        purchases={purchases}
        total={total}
        page={page}
        sizePerPage={sizePerPage}
        onPageChange={onPageChange}
      />
    );
  }

  // Render desktop DataGrid view
  return (
    <>
      <ColumnControlForm>
        <Stack sx={{ padding: '1em' }}>{columnControl}</Stack>
      </ColumnControlForm>
      <DataGrid
        columnVisibilityModel={visibleColumns}
        rows={purchases.map((p) => ({ ...p, customerId }))}
        rowCount={total}
        columns={columns}
        pagination
        paginationMode="server"
        paginationModel={{
          page: page - 1,
          pageSize: sizePerPage,
        }}
        pageSizeOptions={[sizePerPage]}
        classes={{
          root: 'T-packages-container',
          virtualScrollerContent: 'T-packages-table',
        }}
        slots={{ pagination: GridPagination }}
        onPaginationModelChange={({ page }) => onPageChange(page + 1)}
        getRowHeight={() => 'auto'}
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        autoHeight
      />
    </>
  );
}
